import { getConfigManager } from '../../config';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import { Decimal, DEFAULT_DECIMAL_PRECISION } from '../../datastructures';
import { AssetConfig, Nullable } from '../../interfaces';
import { MultiCallContract } from '../../services';
import { configAtom } from '../configAtom';
import { createAsyncAtom } from '../../lib/state';
import { RpcProvider } from 'starknet';
import rpcProviderAtom from '../rpcProviderAtom';

export interface CollateralData {
  collateralFactor: Decimal;
}

export type CollateralDataMap = { [assetAddress: string]: CollateralData };

const fetchData = async (assets: AssetConfig[], provider: RpcProvider) => {
  try {
    const config = getConfigManager().getConfig();

    let result: Nullable<bigint[]> = null;

    try {
      const RESPONSE_CHUNK_SIZE = 5;

      const calldata = assets
        .filter(asset => asset.assetTier !== 'isolated' && asset.assetTier !== 'cross')
        .map(asset => {
          return {
            contractAddress: config.cdpManager,
            entrypoint: 'collateral_data',
            calldata: [asset.address],
            userData: {
              assetAddress: asset.address,
            },
          };
        });

      const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

      result = await multiCallContract.aggregate(calldata);

      if (!result) {
        return null;
      }

      const collateralDataMap: CollateralDataMap = assets.reduce(
        (map, asset) => ({
          ...map,
          [asset.address]: {
            collateralFactor: Decimal.ZERO,
          },
        }),
        {},
      );

      for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
        // Index 2,3 is for collateralFactor
        const collateralFactor = new Decimal({ low: result[i + 2], high: result[i + 3] }, DEFAULT_DECIMAL_PRECISION);

        const assetAddress = calldata[i / RESPONSE_CHUNK_SIZE].userData.assetAddress;

        collateralDataMap[assetAddress] = {
          collateralFactor,
        };
      }

      return collateralDataMap;
    } catch (error) {
      console.warn('Failed to getCollateralData using v2, trying v1...');

      const RESPONSE_CHUNK_SIZE = 6;

      const calldata = assets
        .filter(asset => asset.assetTier !== 'isolated' && asset.assetTier !== 'cross')
        .map(asset => {
          return {
            contractAddress: config.cdpManager,
            entrypoint: 'getCollateralData',
            calldata: [asset.address],
            userData: {
              assetAddress: asset.address,
            },
          };
        });

      const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

      result = await multiCallContract.aggregate(calldata);

      if (!result) {
        return null;
      }

      const collateralDataMap: CollateralDataMap = assets.reduce(
        (map, asset) => ({
          ...map,
          [asset.address]: {
            collateralFactor: Decimal.ZERO,
          },
        }),
        {},
      );

      for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
        // Index 3,4 is for collateralFactor
        const collateralFactor = new Decimal({ low: result[i + 3], high: result[i + 4] }, DEFAULT_DECIMAL_PRECISION);

        const assetAddress = calldata[i / RESPONSE_CHUNK_SIZE].userData.assetAddress;

        collateralDataMap[assetAddress] = {
          collateralFactor,
        };
      }

      return collateralDataMap;
    }
  } catch (error) {
    console.error(`useCollateralData - Failed to load collateral data!`, error);
    return null;
  }
};

const collateralDataAtom = createAsyncAtom(async get => {
  const config = await get(configAtom);
  const provider = await get(rpcProviderAtom);

  const assetsConfig = Object.entries(config.assets).map(([, assetConfig]) => assetConfig);

  return fetchData(assetsConfig, provider);
});
export default collateralDataAtom;
